/**
 * PATH: packages/ui/src/components/find/useInPageFind.ts
 * FILE_ID: UI-FIND-USEINPAGEFIND-001
 * PURPOSE: One-call wiring for an in-page find surface: registers a
 *          FindController (so the shell's single Cmd+F authority opens
 *          THIS surface), owns the bar open/query state, runs the
 *          highlighter over the surface's container, and returns the
 *          props a consumer spreads into <InPageFindBar>.
 * WHY:     Keeps consumers (EmailReader, EmailPreviewPane, kanban board)
 *          to ~3 lines: a container ref + this hook + the bar. All the
 *          registry / highlight / cycling plumbing stays here.
 * FLOW:
 *   ⌘F (shell) → registry → open() → isOpen=true → useInPageHighlight
 *      paints matches → bar shows count + cycles.
 * DEPENDENCIES:
 *   - ./findScopeRegistry (useFindController)
 *   - ./useInPageHighlight (the DOM highlight orchestration)
 *   - ./InPageFindBar (the props shape we return)
 */
import { useCallback, useState, type RefObject } from 'react'
import { useFindController } from './findScopeRegistry'
import { useInPageHighlight } from './useInPageHighlight'
import type { InPageFindBarProps } from './InPageFindBar'

export interface UseInPageFindOptions {
  /** The element whose text nodes get highlighted (e.g. the email body
   *  container, the kanban board scroll region). */
  containerRef: RefObject<HTMLElement | null>
  /** Stable controller id (debug/telemetry), e.g. 'email-reader'. */
  id: string
  /** Surface label shown in the bar placeholder ('email', 'board'). */
  label?: string
  /** When false, the surface neither claims ⌘F nor highlights. Use for
   *  hidden/background instances. Defaults true. */
  enabled?: boolean
}

export interface UseInPageFindResult {
  /** Whether the find bar is open (consumer can also gate side-UI). */
  isOpen: boolean
  /** Open the find bar programmatically (e.g. a toolbar search-icon
   *  button) — same entry as the shell's ⌘F path. Optional seed query.
   *  ADDITIVE (2026-07-09, G5 find-in-thread): existing consumers keep
   *  working untouched. */
  open: (seed?: string) => void
  /** Spread straight into <InPageFindBar {...bar} />. */
  bar: InPageFindBarProps
}

/**
 * Wire an in-page find surface. Returns `{ isOpen, bar }`; render
 * `<InPageFindBar {...bar} />` somewhere in the surface.
 */
export function useInPageFind(opts: UseInPageFindOptions): UseInPageFindResult {
  const { containerRef, id, label, enabled = true } = opts
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')

  const open = useCallback((seed?: string) => {
    setIsOpen(true)
    if (typeof seed === 'string') setQuery(seed)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setQuery('')
  }, [])

  // Claim ⌘F for this surface (most-recently-mounted wins) while enabled.
  useFindController({ id, label, enabled, open })

  const { count, activeIndex, next, prev } = useInPageHighlight(
    containerRef,
    query,
    isOpen && enabled,
  )

  return {
    isOpen,
    open,
    bar: {
      open: isOpen,
      query,
      onQueryChange: setQuery,
      count,
      activeIndex,
      onNext: next,
      onPrev: prev,
      onClose: close,
      label,
    },
  }
}
